import { EMPLOYEE_ROLE, PARENT_ROLE } from 'containers/User/constants.js';
import { getCurrentRole } from 'containers/User/UserProvider/userStorage.js';
import { idValues } from 'utils/arrays.js';

function asUserList(field) {
  if (!field) {
    return [];
  }
  return Array.isArray(field) ? field : idValues(field);
}

function getDisplayName(user) {
  if (!user) {
    return null;
  }
  return typeof user === 'string' ? user : user.displayName;
}

function getCommunityNames(communities) {
  return (communities || []).map((c) => c.name).filter(Boolean);
}

function getGroupIds(communities, groupIds) {
  if (groupIds?.length) {
    return groupIds;
  }
  return (communities || []).map((c) => c.groupId).filter(Boolean);
}

function connectionMatchesGroup(connection, groupIds, communityNames) {
  if (connection.groupIds?.some((id) => groupIds.includes(id))) {
    return true;
  }
  if (!communityNames.length || !connection.groups?.length) {
    return false;
  }
  return connection.groups.some((group) => {
    const name = typeof group === 'string' ? group : group.name;
    return communityNames.includes(name);
  });
}

function filterConnectionsByGroup(connections, communities, groupIds) {
  const gids = getGroupIds(communities, groupIds);
  const communityNames = getCommunityNames(communities);
  if (!gids.length && !communityNames.length) {
    return connections;
  }
  return connections.filter((connection) => connectionMatchesGroup(connection, gids, communityNames));
}

function getGroupChildUsers(user, { communities, groupIds } = {}) {
  const fromChildren = asUserList(user.children);
  if (fromChildren.length) {
    return fromChildren;
  }
  const fromGroupChildren = asUserList(user.groupChildren);
  if (fromGroupChildren.length) {
    return fromGroupChildren;
  }
  const connections = asUserList(user.connections);
  return filterConnectionsByGroup(connections, communities, groupIds);
}

/**
 * Returns display names of children linked to a parent in the context of the given group(s).
 * Only employees (teachers) may see child names; for any other viewer nothing is returned.
 * Falls back gracefully when child data is unavailable.
 */
function getGroupChildNames(user, context = {}) {
  if (!user || user.roleType !== PARENT_ROLE) {
    return [];
  }
  if (getCurrentRole()?.type !== EMPLOYEE_ROLE) {
    return [];
  }
  return getGroupChildUsers(user, context).map(getDisplayName).filter(Boolean);
}

export default getGroupChildNames;
